import React from 'react';
import { motion } from 'framer-motion';
import { Shield, X, Check, Zap } from 'lucide-react';

function CompetitionSection({ content }) {
  const competitors = [
    {
      name: 'StyleSeat / Booksy',
      what: 'Booking platforms',
      gap: 'No job marketplace, no professional network',
      hasJobs: false,
      hasNetwork: false,
      hasBooking: true,
      hasChairSwap: false
    },
    {
      name: 'LinkedIn',
      what: 'Professional network',
      gap: 'Not beauty-specific, no booking/marketplace',
      hasJobs: true,
      hasNetwork: true,
      hasBooking: false,
      hasChairSwap: false
    },
    {
      name: 'Indeed / ZipRecruiter',
      what: 'Job boards',
      gap: 'No industry specialization, no professional profiles',
      hasJobs: true,
      hasNetwork: false,
      hasBooking: false,
      hasChairSwap: false
    },
    {
      name: 'Instagram',
      what: 'Social media',
      gap: 'No business tools, algorithm hides content',
      hasJobs: false,
      hasNetwork: false,
      hasBooking: false,
      hasChairSwap: false
    },
  ];

  const advantages = [
    'Only platform combining job connect + professional network + booking',
    'Chair-swapping marketplace with zero competition',
    'KoreCode viral sharing system for organic growth',
    'Talent autonomy as key differentiator from salon software',
    'First mover in beauty industry professional networking',
  ];

  return (
    <section className="min-h-[calc(100vh-5rem)] flex items-center justify-center px-6 py-20">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-lavender/10 border border-accent-lavender/20 rounded-full mb-6">
            <Shield className="w-4 h-4 text-accent-lavender" />
            <span className="text-sm text-accent-lavender">Competitive Landscape</span>
          </div>
          <h2 className="section-title mb-4">
            Differentiated <span className="text-gradient">Position</span>
          </h2>
          <p className="section-subtitle mx-auto">
            No single platform addresses the complete needs of beauty professionals.
          </p>
        </motion.div>

        {/* Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card overflow-hidden mb-8"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-border">
                  <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Competitor</th>
                  <th className="text-center py-4 px-3 text-sm font-medium text-zinc-400">Jobs</th>
                  <th className="text-center py-4 px-3 text-sm font-medium text-zinc-400">Network</th>
                  <th className="text-center py-4 px-3 text-sm font-medium text-zinc-400">Booking</th>
                  <th className="text-center py-4 px-3 text-sm font-medium text-zinc-400">Chair-Swap</th>
                  <th className="text-left py-4 px-4 text-sm font-medium text-zinc-400">Gap</th>
                </tr>
              </thead>
              <tbody>
                {competitors.map((comp, index) => (
                  <motion.tr
                    key={comp.name}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="border-b border-surface-border/50 last:border-0"
                  >
                    <td className="py-4 px-4">
                      <div className="font-medium text-white">{comp.name}</div>
                      <div className="text-xs text-zinc-500">{comp.what}</div>
                    </td>
                    <td className="text-center py-4 px-3">
                      {comp.hasJobs ? (
                        <Check className="w-5 h-5 text-accent-mint mx-auto" />
                      ) : (
                        <X className="w-5 h-5 text-zinc-600 mx-auto" />
                      )}
                    </td>
                    <td className="text-center py-4 px-3">
                      {comp.hasNetwork ? (
                        <Check className="w-5 h-5 text-accent-mint mx-auto" />
                      ) : (
                        <X className="w-5 h-5 text-zinc-600 mx-auto" />
                      )}
                    </td>
                    <td className="text-center py-4 px-3">
                      {comp.hasBooking ? (
                        <Check className="w-5 h-5 text-accent-mint mx-auto" />
                      ) : (
                        <X className="w-5 h-5 text-zinc-600 mx-auto" />
                      )}
                    </td>
                    <td className="text-center py-4 px-3">
                      <X className="w-5 h-5 text-zinc-600 mx-auto" />
                    </td>
                    <td className="py-4 px-4 text-sm text-zinc-400">{comp.gap}</td>
                  </motion.tr>
                ))}
                {/* KoreFolio Row */}
                <motion.tr
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 }}
                  className="bg-kore-500/10"
                >
                  <td className="py-4 px-4">
                    <div className="font-bold text-kore-400">KoreFolio</div>
                    <div className="text-xs text-kore-300">Complete Platform</div>
                  </td>
                  <td className="text-center py-4 px-3">
                    <Check className="w-5 h-5 text-accent-mint mx-auto" />
                  </td>
                  <td className="text-center py-4 px-3">
                    <Check className="w-5 h-5 text-accent-mint mx-auto" />
                  </td>
                  <td className="text-center py-4 px-3">
                    <Check className="w-5 h-5 text-accent-mint mx-auto" />
                  </td>
                  <td className="text-center py-4 px-3">
                    <Check className="w-5 h-5 text-accent-mint mx-auto" />
                  </td>
                  <td className="py-4 px-4 text-sm text-kore-300 font-medium">All-in-one ecosystem</td>
                </motion.tr>
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Advantages */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="card bg-gradient-to-r from-surface-card to-accent-mint/10"
        >
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-accent-mint" />
            Competitive Advantages
          </h3>
          <div className="grid md:grid-cols-2 gap-3">
            {advantages.map((adv, index) => (
              <motion.div
                key={adv}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9 + index * 0.1 }}
                className="flex items-start gap-2"
              >
                <Check className="w-4 h-4 text-accent-mint flex-shrink-0 mt-0.5" />
                <span className="text-sm text-zinc-300">{adv}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default CompetitionSection;
